import React, { useEffect, useState } from "react";
import { Clock, User, AlertCircle, Loader, Play, Trash2, MessageSquare } from "lucide-react";
import apiClient from "../../../utils/apiClient";
import server from "../../../utils/serverConfig";
import toast from "react-hot-toast";
import AddUserWizard from "./AddUserWizard";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";

const brokerColors = {
  fyers: "bg-blue-800 text-white",
  dhan_hq: "bg-emerald-800 text-white",
  nubra: "bg-purple-800 text-white",
  zerodha: "bg-red-800 text-white",
  mosl: "bg-amber-800 text-white",
};

export default function PendingOnboarding({ onCountChange }) {
  const [pendingList, setPendingList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [resumeDraftId, setResumeDraftId] = useState(null);

  const fetchPendingList = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get(`${server.godseye.baseUrl}crm/onboarding/pending`);
      if (response.data) {
        setPendingList(response.data);
        if (onCountChange) onCountChange(response.data.length);
      }
    } catch (error) {
      console.error("Failed to fetch pending onboardings:", error);
      toast.error("Failed to load pending applications");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingList();
  }, []);

  const [deleteTarget, setDeleteTarget] = useState(null);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await apiClient.delete(`${server.godseye.baseUrl}crm/onboarding/${deleteTarget.id}`);
      toast.success("Draft deleted successfully");
      setDeleteTarget(null);
      fetchPendingList();
    } catch (error) {
      console.error("Failed to delete draft:", error);
      toast.error("Failed to delete draft");
    }
  };

  const handleWizardClose = (success) => {
    setResumeDraftId(null);
    fetchPendingList();
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <Loader className="w-8 h-8 text-blue-500 animate-spin mb-3" />
        <p className="text-zinc-400 text-sm">Loading pending applications...</p>
      </div>
    );
  }

  if (pendingList.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <div className="w-16 h-16 rounded-full bg-zinc-800/60 flex items-center justify-center mb-4">
          <Clock className="w-7 h-7 text-zinc-600" />
        </div>
        <p className="text-zinc-400 text-sm font-medium">No pending onboardings</p>
        <p className="text-zinc-600 text-xs mt-1">All applications have been processed</p>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center gap-2 mb-4">
        <AlertCircle className="w-4 h-4 text-amber-400" />
        <p className="text-amber-400/80 text-xs font-medium">
          {pendingList.length} application{pendingList.length !== 1 ? "s" : ""} pending review
        </p>
      </div>

      <div className="space-y-3">
        {pendingList.map((item) => {
          // Calculate step progress
          let progress = 0;
          let stepText = "Step 1 of 3: Basic Info pending";
          
          if (item.is_broker_added) {
            progress = 100;
            stepText = "Onboarding Completed";
          } else {
            if (item.is_basic_info_complete) {
              progress = 33;
              stepText = "Step 2 of 3: Legalities pending";
            }
            
            if (item.is_onhold_leegality && !item.is_legalities_complete) {
              if (item.is_approve_req_sent && !item.admin_approval) {
                progress = 90;
                stepText = "Submitted for Approval (Compliance Pending)";
              } else {
                progress = 33;
                stepText = "Step 3 of 3: Broker Details pending & 2nd Pending(Bypass)";
              }
            } else if (item.is_legalities_complete) {
              if (item.is_approve_req_sent && !item.admin_approval) {
                progress = 95;
                stepText = "Submitted for Approval (Pending Review)";
              } else {
                progress = 66;
                stepText = "Step 3 of 3: Broker Details pending";
              }
            }
          }

          // Active products list
          const activeProducts = [];
          if (item.product_is_optimus) activeProducts.push("Optimus");
          if (item.product_is_pledgeplus) activeProducts.push("PledgePlus");
          if (item.product_is_polaris) activeProducts.push("Polaris");
          if (item.product_is_polaris_lite) activeProducts.push("Polaris Lite");

          // Formatted date
          const dateStr = item.updated_at 
            ? new Date(item.updated_at).toLocaleDateString("en-IN", {
                day: "numeric",
                month: "short",
                year: "numeric"
              })
            : "-";

          return (
            <div
              key={item.id}
              className="flex flex-col sm:flex-row sm:items-start justify-between p-4 bg-zinc-900/40 border border-zinc-800/60 rounded-xl hover:border-zinc-700/60 transition-all duration-200 group gap-4"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-zinc-800/80 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <User className="w-5 h-5 text-zinc-500" />
                </div>
                <div className="space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-white text-sm font-medium">{item.name || "Unnamed Draft"}</p>
                    {activeProducts.map((p) => (
                      <span key={p} className="px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/20 text-[9px] font-semibold">
                        {p}
                      </span>
                    ))}
                    {item.notes && item.notes.includes("[Rejected]") && (
                      <span className="px-1.5 py-0.5 rounded bg-red-500/10 text-red-400 border border-red-500/20 text-[9px] font-semibold">
                        Rejected by Approver
                      </span>
                    )}
                  </div>
                  <p className="text-zinc-500 text-xs">{item.email || "No email"}</p>
                  
                  {/* Progress bar */}
                  <div className="pt-2 flex items-center gap-3 w-64">
                    <div className="flex-1 bg-zinc-800 rounded-full h-1.5 overflow-hidden">
                      <div 
                        className="bg-blue-500 h-full rounded-full transition-all duration-500"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <span className="text-[10px] text-zinc-400 whitespace-nowrap">{stepText}</span>
                  </div>

                  {/* Rejection/Admin Notes */}
                  {item.notes && item.notes.trim() && (
                    <div className={`mt-2 text-[11px] p-2 rounded border max-w-md ${
                      item.notes.includes("[Rejected]")
                        ? "text-red-400 bg-red-950/20 border-red-900/30"
                        : "text-zinc-400 bg-zinc-800/20 border-zinc-700/40"
                    }`}>
                      <span className="break-words whitespace-pre-wrap">{item.notes.trim()}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between sm:justify-end gap-3 flex-shrink-0">
                <div className="flex items-center gap-2">
                  {item.is_broker_added ? (
                    <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                      Completed
                    </span>
                  ) : (
                    item.is_approve_req_sent && !item.admin_approval && (
                      <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-amber-500/10 text-amber-400 border border-amber-500/20">
                        Under Review
                      </span>
                    )
                  )}
                  {item.broker && (
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${brokerColors[item.broker.toLowerCase().replace(/[^a-z0-9_]/g, "").replace("dhanhq", "dhan_hq")] || "bg-zinc-700 text-white"}`}>
                      {item.broker}
                    </span>
                  )}
                  <span className="text-zinc-500 text-xs">{dateStr}</span>
                </div>
                
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setResumeDraftId(item.id)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-600 hover:bg-blue-500 text-white transition-all"
                  >
                    <Play className="w-3 h-3 fill-white" />
                    Resume
                  </button>
                  <button
                    onClick={() => setDeleteTarget({ id: item.id, name: item.name })}
                    className="p-1.5 rounded-lg text-zinc-500 hover:text-red-400 hover:bg-red-500/10 transition-all"
                    title="Delete Draft"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <AddUserWizard
        isOpen={!!resumeDraftId}
        onClose={handleWizardClose}
        resumeDraftId={resumeDraftId}
        handleAddFyers={() => {}}
        handleAddNubra={() => {}}
        handleAddDhanHQ={() => {}}
        handleAddZerodha={() => {}}
        handleAddMOSL={() => {}}
      />

      {/* ── Custom Delete Confirmation Dialog ── */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent className="sm:max-w-[425px] border-zinc-800 bg-[#0f1116] text-white">
          <DialogHeader className="flex flex-col items-center text-center">
            <div className="w-12 h-12 rounded-full bg-red-900/30 border border-red-700/40 flex items-center justify-center mb-3">
              <Trash2 className="w-5 h-5 text-red-400 animate-pulse" />
            </div>
            <DialogTitle className="text-white text-base font-semibold">Delete Onboarding Draft</DialogTitle>
          </DialogHeader>
          
          <div className="py-3 text-center">
            <p className="text-zinc-400 text-sm leading-relaxed">
              Are you sure you want to delete the onboarding draft for{" "}
              <strong className="text-white">{deleteTarget?.name || "Unnamed Draft"}</strong>?
            </p>
            <p className="text-zinc-500 text-xs mt-1.5">
              This action is permanent and cannot be undone.
            </p>
          </div>
          
          <DialogFooter className="mt-4 flex gap-2 sm:gap-0 justify-center sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteTarget(null)}
              className="bg-zinc-800 border-zinc-700 hover:bg-zinc-700 hover:text-white text-zinc-300 text-xs font-medium px-4 py-2"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-500 text-white text-xs font-semibold px-4 py-2"
            >
              Yes, Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
